import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  gamesQuery,
  getGamesSessions,
  scenarioLevelsQuery,
  scoresInsightsQuery
} from './api';

import { popupMessagesSummaryBreakdown } from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';
import Analysis from '../UI/game/analysis';

export default function BalancedEquationSummaryBreakdown() {
  const navigate = useNavigate();
  
  const { 
    getUser,
    getCurrentBalancedEquationGameId
  } = useAuth();

  const user = getUser();
  
  const gameId = getCurrentBalancedEquationGameId();

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  const [scenarioId, setScenarioId] = useState(0);

  const [data, setData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [popupMessages, setPopupMessages] = useState(popupMessagesSummaryBreakdown);
  const textAdded = useRef(false);

  const { data: gamesSessions } = useQuery({
    queryKey: ['gamesSessionsSummaryBreakdown'],
    queryFn: getGamesSessions,
    refetchOnWindowFocus: false,
  });

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery({scenarioId}));
  const { data: scoresInsights } = useQuery(scoresInsightsQuery({gameId, gameSessionId: currentGameSessionId}));

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Balanced Equation') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  const calculateAverage = (data, prop) => {
    if (!data || data.length === 0) return 0;

    let sum = 0;
    let count = 0;

    data.forEach(record => {
      const value = record.accuracy[prop];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };
  
  useEffect(() => {
    if(scoresInsights && scenarioLevels?.length) {
      setDataLoaded(true);
      console.log('scoresInsights', scoresInsights);

      const increaseDecreaseElementsScore = calculateAverage(scoresInsights, 'increase_decrease_elements');
      const debitsCreditsScore = calculateAverage(scoresInsights, 'debits_credits');
      const increaseDecreaseAccountsScore = calculateAverage(scoresInsights, 'increase_decrease_accounts');

      const sectionData = [
        ...(increaseDecreaseElementsScore ? [{ title: "Increase or Decrease (Elements)", score: increaseDecreaseElementsScore * 100, color: '#B4E6EA' }] : []),
        ...(debitsCreditsScore ? [{ title: "Debits and Credits (Elements)", score: debitsCreditsScore * 100, color: '#B4E6EA' }] : []),
        ...(increaseDecreaseAccountsScore ? [{ title: "Accounts Impacted", score: increaseDecreaseAccountsScore * 100, color: '#B4E6EA' }] : []),
      ];
      
      const data = [
        ...(sectionData.length > 0 ? [{
          bars: sectionData,
          barsLabel: '%'
        }] : []),
      ];
      setData(data);
    }
  }, [scoresInsights, scenarioLevels]);
  
  const handlePopupMessagesNextButtonClick = () => {
    navigate('/balanced-equation/summary/decision-breakdown');
  }
  
  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/balanced-equation/summary');
  }

  const maxScore = Math.max(...data.flatMap(section => 
    section.bars?.map(bar => bar.score) || []
  ));

  useEffect(() => {
    if (data.length > 0 && !textAdded.current) {
      const updatedMessages = [...popupMessages];
      let additionalText = '';
      let decisionCounter = 0;
      
      if (data[0].bars.some(item => item.title === "Increase or Decrease (Elements)")) {
        decisionCounter++;
        additionalText += `<h3>Decision ${decisionCounter} | Increase or Decrease (Elements)</h3><p>To demystify double-entry accounting, we started with big picture intuitive thinking: how does a  transaction impact Assets, Liabilities, or Equity. Understanding this lays a strong foundation.</p><br>`;
      }
      
      if (data[0].bars.some(item => item.title === "Debits and Credits (Elements)")) {
        decisionCounter++;
        additionalText += `<h3>Decision ${decisionCounter} | Elements Debited and Credited</h3><p>Once you determine if the Balance Sheet elements increase or decrease, it’s easier to apply debit/credit normal balance rules.</p><br>`;
      }
      
      if (scenarioLevels?.some(l => l.name === 'accounts-impacted')) {
        decisionCounter++;
        additionalText += `<h3>Decision ${decisionCounter} | Accounts Impacted (Increase or Decrease)</h3><p>Next, we zoomed in on the finer details of deciding which specific accounts should be impacted, given the elements affected.</p><br>`;
      }

      if (scenarioLevels?.some(l => l.name === 'income-statement-impact')) {
        decisionCounter++;
        additionalText += `<h3>Decision ${decisionCounter} | Income Statement Impacted</h3><p>Finally, we looked at how revenue and expense related transactions impact the Balance Sheet.</p><br>`;
      }

      if (additionalText) {
        updatedMessages[0] = {
          ...updatedMessages[0],
          text: updatedMessages[0].text + additionalText
        };
        
        setPopupMessages(updatedMessages);
      }
      
      textAdded.current = true;
    }
  }, [data, scenarioLevels]);
  
  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container">
            <PopupMessages
              messages={popupMessages}
              messageSize="small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
            />
            <Analysis
              heading="Accuracy Breakdown"
              data={data}
              size="small"
              maxScore={maxScore}
              isBigScores={true}
              alignBarTitle="center"
            />
          </div>
      }
    </Layout>
  );
}
